import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Loader2, Search, PackageOpen, Check, Trash2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export function ImportedDishesTab({ restaurantId, onIncorporated }) {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [incorporating, setIncorporating] = useState(false);

  const fetchDishes = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const data = await api.getImportedDishes(restaurantId, search, "pending");
      setDishes(data);
    } catch {
      toast.error("Error al cargar platos importados");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, search]);

  useEffect(() => {
    const timer = setTimeout(fetchDishes, 300);
    return () => clearTimeout(timer);
  }, [fetchDishes]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === dishes.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(dishes.map(d => d.imported_dish_id)));
    }
  };

  const handleIncorporate = async () => {
    if (selected.size === 0) return toast.error("Selecciona al menos un plato");
    setIncorporating(true);
    try {
      const result = await api.incorporateImportedDishes(restaurantId, Array.from(selected));
      toast.success(result.message);
      setSelected(new Set());
      fetchDishes();
      onIncorporated?.();
    } catch (err) {
      toast.error(err.message || "Error al incorporar platos");
    } finally {
      setIncorporating(false);
    }
  };

  const handleDismiss = async (dishId) => {
    try {
      await api.dismissImportedDish(restaurantId, dishId);
      setDishes(prev => prev.filter(d => d.imported_dish_id !== dishId));
      selected.delete(dishId);
      setSelected(new Set(selected));
      toast.success("Plato descartado");
    } catch {
      toast.error("Error al descartar plato");
    }
  };

  if (loading && dishes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!loading && dishes.length === 0 && !search) {
    return (
      <div className="text-center py-16" data-testid="imported-dishes-empty">
        <PackageOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
        <p className="text-muted-foreground font-medium">No hay platos importados pendientes</p>
        <p className="text-sm text-muted-foreground mt-1">
          Los platos aparecen aqui tras una importacion masiva de ventas desde Integracion TPV
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="imported-dishes-tab">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar platos importados..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="imported-dishes-search"
          />
        </div>
        <div className="flex gap-2 items-center">
          {dishes.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {dishes.length} pendiente{dishes.length !== 1 ? "s" : ""}
            </Badge>
          )}
          {selected.size > 0 && (
            <Button
              onClick={handleIncorporate}
              disabled={incorporating}
              data-testid="incorporate-selected-btn"
            >
              {incorporating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Incorporar {selected.size} plato{selected.size !== 1 ? "s" : ""}
            </Button>
          )}
        </div>
      </div>

      {/* Select all */}
      {dishes.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <Checkbox
            checked={selected.size === dishes.length && dishes.length > 0}
            onCheckedChange={toggleAll}
            data-testid="select-all-imported"
          />
          <span className="text-sm text-muted-foreground">
            {selected.size === dishes.length ? "Deseleccionar todos" : "Seleccionar todos"}
          </span>
        </div>
      )}

      {/* Dish list */}
      <Card className="border-none shadow-soft">
        <CardContent className="p-0">
          <div className="divide-y">
            {dishes.map(dish => (
              <div
                key={dish.imported_dish_id}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  selected.has(dish.imported_dish_id) ? "bg-primary/5" : "hover:bg-muted/30"
                }`}
                data-testid={`imported-dish-${dish.imported_dish_id}`}
              >
                <Checkbox
                  checked={selected.has(dish.imported_dish_id)}
                  onCheckedChange={() => toggleSelect(dish.imported_dish_id)}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{dish.name}</p>
                  <div className="flex gap-2 mt-0.5">
                    {dish.suggested_price > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {dish.suggested_price.toFixed(2)} EUR
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {dish.sales_count} venta{dish.sales_count !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => handleDismiss(dish.imported_dish_id)}
                  data-testid={`dismiss-imported-${dish.imported_dish_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {!loading && dishes.length === 0 && search && (
        <p className="text-center text-muted-foreground py-8 text-sm">
          No se encontraron platos que coincidan con "{search}"
        </p>
      )}
    </div>
  );
}
